<script lang="ts" setup>
import { DAtaTableInvoicingOk } from '@/components/common/ata/tables/ata-table-invoicing-ok';
import { DAtaTableUnderRemittance } from '@/components/common/ata/tables/ata-table-under-remittance';
import { PhCheckCircle, PhPaperPlaneTilt } from '@phosphor-icons/vue';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

defineProps<{
  projectId: string;
}>();

type Page = 'remittance' | 'invoicing';

const pages: Page[] = ['remittance', 'invoicing'];
const activePage = ref<Page>('remittance');
const slideDirection = ref<'left' | 'right'>('left');

function setPage(page: Page) {
  slideDirection.value =
    pages.indexOf(page) > pages.indexOf(activePage.value) ? 'left' : 'right';
  activePage.value = page;
}
</script>

<template>
  <div class="card">
    <div class="card-header ata-card-header">
      <p class="card-header-title ata-title">
        {{ t('ata.title') }}
      </p>
    </div>
    <div class="ata-tabs">
      <div class="ata-switch">
        <button
          class="ata-switch-option"
          :class="{ 'is-active': activePage === 'remittance' }"
          @click="setPage('remittance')"
        >
          <PhPaperPlaneTilt :size="14" />
          {{ t('ata.tabs.underRemittance') }}
        </button>
        <button
          class="ata-switch-option"
          :class="{ 'is-active': activePage === 'invoicing' }"
          @click="setPage('invoicing')"
        >
          <PhCheckCircle :size="14" />
          {{ t('ata.tabs.invoicingOk') }}
        </button>
      </div>
    </div>
    <div class="card-content p-0 tab-content">
      <Transition :name="`slide-${slideDirection}`" mode="out-in">
        <DAtaTableUnderRemittance
          v-if="activePage === 'remittance'"
          key="remittance"
          class="inner-table"
          @action="$emit('action', $event)"
          :projectId="projectId"
        />
        <DAtaTableInvoicingOk
          v-else
          key="invoicing"
          class="inner-table"
          :projectId="projectId"
          @action="$emit('action', $event)"
        />
      </Transition>
    </div>
  </div>
</template>

<style scoped>
.ata-card-header {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  padding: 0;
  flex-direction: column;
  align-items: stretch;
}

.ata-title {
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #222;
}

.ata-tabs {
  display: flex;
  justify-content: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e0e0e0;
}

.ata-switch {
  display: flex;
  background: #ebebee;
  border-radius: 999px;
  padding: 3px;
  gap: 2px;
}

.ata-switch-option {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 1rem;
  border: none;
  border-radius: 999px;
  background: transparent;
  font-size: 0.8rem;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
}

.ata-switch-option:hover {
  color: #333;
}

.ata-switch-option.is-active {
  background: #fff;
  color: var(--primary-color);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}

/* Strip inner card chrome since we're already in a card */
.inner-table :deep(.card) {
  box-shadow: none;
  border-radius: 0;
}

.inner-table :deep(.card-header) {
  display: none;
}

.inner-table :deep(.card-content) {
  padding: 0;
}

.tab-content {
  max-height: 40rem;
  overflow-x: hidden;
  overflow-y: auto;
}

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
}

.slide-left-enter-from {
  transform: translateX(40px);
  opacity: 0;
}

.slide-left-leave-to {
  transform: translateX(-40px);
  opacity: 0;
}

.slide-right-enter-from {
  transform: translateX(-40px);
  opacity: 0;
}

.slide-right-leave-to {
  transform: translateX(40px);
  opacity: 0;
}
</style>
